if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;

      /* ==============================================================
         PROCTERS CART DRAWER SCROLL LOCK

         Strong scroll lock for mobile/tablet browsers, including Safari.

         The page is frozen at its current scroll position rather than
         merely setting overflow:hidden. This prevents Safari from moving
         the underlying document towards the top while the drawer is open.
         ============================================================== */

      this.pageScrollLocked = false;
      this.lockedScrollX = 0;
      this.lockedScrollY = 0;
      this.scrollLockState = null;
      this.scrollRestoreFrame = null;

      this.setHeaderCartIconAccessibility();
    }

    connectedCallback() {
      /*
       * Nothing is required here at present, but keeping lifecycle cleanup
       * paired with disconnectedCallback makes the component safer if Shopify
       * ever replaces the complete element dynamically.
       */
    }

    disconnectedCallback() {
      if (this.scrollRestoreFrame) {
        cancelAnimationFrame(this.scrollRestoreFrame);
        this.scrollRestoreFrame = null;
      }

      this.unlockPageScroll();
    }

    ensureContent() {
      if (typeof window.loadTemplateContent === 'function') {
        window.loadTemplateContent(this);
      }

      return this.querySelector('.drawer__inner');
    }

    init() {
      if (!this.isInitialized && !this.initializationQueued) {
        this.initializationQueued = true;
        this.ensureContent();

        requestAnimationFrame(() => {
          this.initializationQueued = false;
          if (this.isInitialized) return;

          this.addEventListener(
            'keyup',
            (evt) => evt.code === 'Escape' && this.close()
          );

          const overlay = this.querySelector('#CartDrawer-Overlay');

          if (overlay) {
            overlay.addEventListener(
              'click',
              this.close.bind(this)
            );
          }

          this.moveCssLinks();
          this.isInitialized = true;
        });
      }
    }

    reload() {
      this.ensureContent();

      const sections = this.getSectionsToRender().map(
        (section) => section.id
      );

      return fetch(`?sections=${sections.join(',')}`)
        .then((response) => response.json())
        .then((response) => {
          this.renderContents(
            {
              sections: {
                ...response,
              },
            },
            true
          );

          return response;
        })
        .catch((e) => {
          console.error(e);
          throw e;
        });
    }

    moveCssLinks() {
      const cssLinks = this.querySelectorAll(
        'link[rel="stylesheet"][href*=".css"]'
      );

      cssLinks.forEach((link) => {
        const href = link.href;

        if (!document.head.querySelector(`link[href="${href}"]`)) {
          const newLink = link.cloneNode(true);
          document.head.appendChild(newLink);
        }
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');

      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      this.init();

      const drawerInner = this.ensureContent();

      if (!drawerInner) return;

      if (triggeredBy) {
        this.setActiveElement(triggeredBy);
      }

      const cartDrawerNote = this.querySelector(
        '[id^="Details-"] summary'
      );

      if (
        cartDrawerNote &&
        !cartDrawerNote.hasAttribute('role')
      ) {
        this.setSummaryAccessibility(cartDrawerNote);
      }

      /*
       * Lock BEFORE starting the opening animation.

       * This is important on touch devices because the gesture which opened
       * the drawer must not be allowed to continue moving the page beneath it.
       */
      this.lockPageScroll();

      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.removeDeleteTabIndex();

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn =
            this.classList.contains('is-empty')
              ? this.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');

          const focusElement =
            this.querySelector('.drawer__inner') ||
            this.querySelector('.drawer__close');

          if (
            containerToTrapFocusOn &&
            focusElement &&
            typeof trapFocus === 'function'
          ) {
            trapFocus(
              containerToTrapFocusOn,
              focusElement
            );
          }

          if (
            typeof publish === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined'
          ) {
            publish(PUB_SUB_EVENTS.cartOpen);
          }
        },
        { once: true }
      );
    }

    close() {
      this.classList.remove('active');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement);
      }

      this.unlockPageScroll();

      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        publish(PUB_SUB_EVENTS.cartClose);
      }
    }

    /* ==============================================================
       STRONG PAGE LOCK
       ============================================================== */

    lockPageScroll() {
      if (this.pageScrollLocked) return;

      const html = document.documentElement;
      const body = document.body;

      if (!html || !body) return;

      this.pageScrollLocked = true;

      this.lockedScrollX =
        window.pageXOffset ||
        document.documentElement.scrollLeft ||
        0;

      this.lockedScrollY =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;

      /*
       * Store ALL inline properties we are about to touch.
       *
       * This means another theme component can legitimately have styles on
       * html/body and we will restore them exactly rather than guessing.
       */
      this.scrollLockState = {
        htmlOverflow: html.style.overflow,
        htmlOverscrollBehavior:
          html.style.overscrollBehavior,
        htmlScrollBehavior:
          html.style.scrollBehavior,

        bodyOverflow: body.style.overflow,
        bodyOverscrollBehavior:
          body.style.overscrollBehavior,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
      };

      html.classList.add(
        'ph-cart-drawer-locked'
      );

      body.classList.add(
        'ph-cart-drawer-locked'
      );

      /*
       * First stop the root element itself from scrolling.
       */
      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';

      /*
       * Disable smooth scroll while the document is frozen. Otherwise a theme
       * with scroll-behavior:smooth can animate the restoration when the cart
       * closes.
       */
      html.style.scrollBehavior = 'auto';

      /*
       * Then physically freeze the body at its current visual position.
       *
       * Safari may internally consider the root scroll position to be zero
       * once the body becomes fixed. The negative top offset keeps the page
       * visually in exactly the same place.
       */
      body.style.position = 'fixed';
      body.style.top = `-${this.lockedScrollY}px`;
      body.style.left = `-${this.lockedScrollX}px`;
      body.style.right = '0';
      body.style.width = '100%';

      body.style.overflow = 'hidden';
      body.style.overscrollBehavior = 'none';
    }

    unlockPageScroll() {
      if (!this.pageScrollLocked) return;

      const html = document.documentElement;
      const body = document.body;

      if (!html || !body) {
        this.pageScrollLocked = false;
        this.scrollLockState = null;
        return;
      }

      const state = this.scrollLockState || {};
      const restoreX = this.lockedScrollX;
      const restoreY = this.lockedScrollY;

      const restore = (
        element,
        property,
        value
      ) => {
        if (value !== undefined && value !== null && value !== '') {
          element.style.setProperty(
            property,
            value
          );
        } else {
          element.style.removeProperty(
            property
          );
        }
      };

      /*
       * Restore the body first.
       *
       * Keep html scroll-behaviour forced to auto until AFTER scrollTo,
       * otherwise global smooth scrolling can animate the page back into place.
       */

      restore(
        body,
        'overflow',
        state.bodyOverflow
      );

      restore(
        body,
        'overscroll-behavior',
        state.bodyOverscrollBehavior
      );

      restore(
        body,
        'position',
        state.bodyPosition
      );

      restore(
        body,
        'top',
        state.bodyTop
      );

      restore(
        body,
        'left',
        state.bodyLeft
      );

      restore(
        body,
        'right',
        state.bodyRight
      );

      restore(
        body,
        'width',
        state.bodyWidth
      );

      restore(
        html,
        'overflow',
        state.htmlOverflow
      );

      restore(
        html,
        'overscroll-behavior',
        state.htmlOverscrollBehavior
      );

      html.classList.remove(
        'ph-cart-drawer-locked'
      );

      body.classList.remove(
        'ph-cart-drawer-locked'
      );

      /*
       * Body fixation causes the browser's real root scroll coordinate to
       * change on some Safari versions. Put it straight back before allowing
       * normal scrolling again.
       */
      window.scrollTo(
        restoreX,
        restoreY
      );

      /*
       * Restore whatever scroll-behaviour the theme had on the next frame.
       * The immediate scroll restoration above therefore cannot animate.
       */
      if (this.scrollRestoreFrame) {
        cancelAnimationFrame(
          this.scrollRestoreFrame
        );
      }

      this.scrollRestoreFrame =
        requestAnimationFrame(() => {
          restore(
            html,
            'scroll-behavior',
            state.htmlScrollBehavior
          );

          this.scrollRestoreFrame = null;
        });

      this.pageScrollLocked = false;
      this.lockedScrollX = 0;
      this.lockedScrollY = 0;
      this.scrollLockState = null;
    }

    /** Ensure all remove buttons can be tabbed to. */
    removeDeleteTabIndex() {
      this.querySelectorAll(
        '.cart-remove-button'
      ).forEach((button) => {
        button.removeAttribute('tabindex');
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute(
        'role',
        'button'
      );

      cartDrawerNote.setAttribute(
        'aria-expanded',
        'false'
      );

      if (
        cartDrawerNote.nextElementSibling &&
        cartDrawerNote.nextElementSibling.getAttribute('id')
      ) {
        cartDrawerNote.setAttribute(
          'aria-controls',
          cartDrawerNote.nextElementSibling.id
        );
      }

      cartDrawerNote.addEventListener(
        'click',
        (event) => {
          event.currentTarget.setAttribute(
            'aria-expanded',
            !event.currentTarget
              .closest('details')
              .hasAttribute('open')
          );
        }
      );

      if (
        cartDrawerNote.parentElement &&
        typeof onKeyUpEscape === 'function'
      ) {
        cartDrawerNote.parentElement.addEventListener(
          'keyup',
          onKeyUpEscape
        );
      }
    }

    renderContents(
      parsedState,
      preventOpen = false
    ) {
      const drawerInner = this.ensureContent();

      if (
        !drawerInner ||
        !parsedState ||
        !parsedState.sections
      ) {
        return false;
      }

      const drawerSectionHtml =
        parsedState.sections['cart-drawer'];

      if (drawerSectionHtml) {
        const sourceDrawer =
          this.getSectionDOM(
            drawerSectionHtml,
            'cart-drawer'
          );

        if (sourceDrawer) {
          this.classList.toggle(
            'is-empty',
            sourceDrawer.classList.contains(
              'is-empty'
            )
          );
        } else if (
          typeof parsedState.item_count === 'number'
        ) {
          this.classList.toggle(
            'is-empty',
            parsedState.item_count === 0
          );
        } else {
          this.classList.remove('is-empty');
        }
      }

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach(
        (section) => {
          const sectionElement =
            section.selector
              ? document.querySelector(
                  section.selector
                )
              : document.getElementById(
                  section.id
                );

          const sectionHtml =
            parsedState.sections[
              section.id
            ];

          if (
            !sectionElement ||
            !sectionHtml
          ) {
            return;
          }

          const newHtml =
            this.getSectionInnerHTML(
              sectionHtml,
              section.selector
            );

          if (newHtml == null) return;

          sectionElement.innerHTML =
            newHtml;
        }
      );

      setTimeout(() => {
        this.ensureContent();

        const overlay =
          this.querySelector(
            '#CartDrawer-Overlay'
          );

        if (overlay) {
          overlay.addEventListener(
            'click',
            this.close.bind(this)
          );
        }

        this.removeDeleteTabIndex();
      });

      this.removeDeleteTabIndex();

      if (
        theme.settings.cartDrawer.atcAction ===
          'drawer' &&
        !preventOpen
      ) {
        setTimeout(() => {
          this.open();
        });
      }

      return true;
    }

    getSectionInnerHTML(
      html,
      selector = '.shopify-section'
    ) {
      if (!html) return null;

      const cleanedHtml = html.replace(
        /<template\b[^>]*>|<\/template>/g,
        ''
      );

      const element =
        new DOMParser()
          .parseFromString(
            cleanedHtml,
            'text/html'
          )
          .querySelector(selector);

      return element
        ? element.innerHTML
        : null;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(
      html,
      selector = '.shopify-section'
    ) {
      if (!html) return null;

      return new DOMParser()
        .parseFromString(
          html,
          'text/html'
        )
        .querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

if (!customElements.get('cart-drawer-items')) {
  customElements
    .whenDefined('cart-items')
    .then(() => {
      class CartDrawerItems extends CartItems {
        getSectionsToRender() {
          return [
            {
              id: 'CartDrawer',
              section: 'cart-drawer',
              selector: '.drawer__inner',
            },
            {
              id: 'cart-icon-bubble',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            },
          ];
        }
      }

      customElements.define(
        'cart-drawer-items',
        CartDrawerItems
      );
    });
}

/* Keep the underlying cart form complete after a visual bundle row replaces
   one component's native quantity UI. This preserves one updates[] value per
   real cart line, so checkout and any no-JS cart form submission cannot split
   or mis-map the bundle components. */
(function () {
  if (
    window.__proctersBundleFormQuantitySync
  ) {
    return;
  }

  window.__proctersBundleFormQuantitySync =
    true;

  function syncBundleFormQuantities() {
    document
      .querySelectorAll(
        '.ph-cart-bundle__quantity-shell'
      )
      .forEach((shell) => {
        const visibleInput =
          shell.querySelector(
            '[data-ph-bundle-quantity]'
          );

        if (!visibleInput) return;

        let nativeInput =
          shell.querySelector(
            '[data-ph-bundle-native-quantity]'
          );

        if (!nativeInput) {
          nativeInput =
            document.createElement(
              'input'
            );

          nativeInput.type = 'hidden';
          nativeInput.name = 'updates[]';

          nativeInput.setAttribute(
            'data-ph-bundle-native-quantity',
            ''
          );

          shell.prepend(nativeInput);
        }

        nativeInput.value =
          visibleInput.value;
      });
  }

  if (
    typeof MutationObserver !==
    'undefined'
  ) {
    const observer =
      new MutationObserver(
        syncBundleFormQuantities
      );

    const start = () => {
      if (!document.body) return;

      observer.observe(
        document.body,
        {
          childList: true,
          subtree: true,
        }
      );

      syncBundleFormQuantities();
    };

    if (
      document.readyState ===
      'loading'
    ) {
      document.addEventListener(
        'DOMContentLoaded',
        start,
        { once: true }
      );
    } else {
      start();
    }
  }

  document.addEventListener(
    'input',
    (event) => {
      if (
        event.target &&
        event.target.matches(
          '[data-ph-bundle-quantity]'
        )
      ) {
        syncBundleFormQuantities();
      }
    }
  );
})();